import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
// For Amplify v6, we import auth functions directly from aws-amplify/auth
import { signIn, signUp, signOut, getCurrentUser } from 'aws-amplify/auth';
import { AuthState, User } from '../types';
import { configureAmplify } from '../config/aws-config';

// Initialize Amplify configuration
configureAmplify();

interface AuthContextType {
  authState: AuthState;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const defaultAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: true,
  error: null
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(defaultAuthState);

  useEffect(() => {
    // Check if user is logged in using Amplify Auth
    const checkAuth = async () => {
      try {
        setAuthState(prev => ({ ...prev, isLoading: true }));
        
        try {
          // Get current authenticated user
          const { username, userId, signInDetails } = await getCurrentUser();
          
          // Map Cognito user to our app's user type
          const user: User = {
            id: userId,
            username: username,
            email: signInDetails?.loginId || username // Use loginId as email if available
          };
          
          setAuthState({
            isAuthenticated: true,
            user,
            isLoading: false,
            error: null
          });
        } catch {
          // No current authenticated user
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: null
          });
        }
      } catch (error) {
        // No current authenticated user
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: null
        });
      }
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Attempt to sign in with Amplify Auth
      const { isSignedIn, nextStep } = await signIn({ username, password });
      
      if (isSignedIn) {
        // Get current user
        const { username: userId, userId: id, signInDetails } = await getCurrentUser();
        
        // Create user object
        const user: User = {
          id,
          username: userId,
          email: signInDetails?.loginId || username
        };
        
        setAuthState({
          isAuthenticated: true,
          user,
          isLoading: false,
          error: null
        });
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: `Login requires additional steps: ${nextStep.signInStep}`
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: 'Login failed. Please check your credentials.'
      });
    }
  };

  const signup = async (username: string, email: string, password: string): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Sign up with Amplify Auth
      const { isSignUpComplete, userId } = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email
          }
        }
      });
      
      if (isSignUpComplete) {
        // In real implementation, might need to handle verification
        // For now, create a basic user
        const user: User = {
          id: userId || '',
          username,
          email
        };
        
        setAuthState({
          isAuthenticated: true,
          user,
          isLoading: false,
          error: null
        });
      } else {
        // Signup requires confirmation
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: 'Please confirm your signup'
        });
      }
    } catch (error) {
      console.error('Signup error:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: 'Signup failed. Please try again.'
      });
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      // Sign out with Amplify Auth
      await signOut();
      
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null
      });
    } catch (error) {
      console.error('Logout error:', error);
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Logout failed. Please try again.'
      }));
    }
  };

  const value: AuthContextType = {
    authState,
    login,
    signup,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
